import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import type {
  Nullable,
  TournamentPlayerStatus,
  UserWithToken,
  WithData,
} from './api.types';

/**
 * - `OK`: aceptado y anotado en Dueling Book.
 * - `NO_DB_USER`: aceptado sin usuario de Dueling Book cargado.
 * - `NOT_IN_DB`: aceptado con usuario, pero no anotado en Dueling Book.
 * - `UNMATCHED`: anotado en Dueling Book sin coincidir con nadie aceptado; el
 *   sync aborta.
 */
export type DuelingBookPlayerState = 'OK' | 'NO_DB_USER' | 'NOT_IN_DB' | 'UNMATCHED';

export type DuelingBookPlayerRow = {
  state: DuelingBookPlayerState;
  userId: number | null;
  name: string | null;
  ygoaStatus: TournamentPlayerStatus | null;
  duelingBookId: string | null;
  dbUsername: string | null;
};

export type DuelingBookStatus = {
  tournamentId: number;
  tourneyId: string | null;
  tourneyUrl: string | null;
  challongeUrl: string | null;
  remote: {
    name: string | null;
    started: boolean;
    ended: boolean;
    participants: number;
    lastRound: number | null;
    error: string | null;
  } | null;
  players: DuelingBookPlayerRow[];
  pendingInvitations: number;
  lastSyncAt: string | null;
  syncedMatches: number;
};

export type DuelingBookSyncResult = {
  created: number;
  updated: number;
  currentRound: number;
};

/** Estado del torneo de Dueling Book vinculado y cruce de jugadores. Admin. */
export const getDuelingBookStatus = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<DuelingBookStatus | null>>(
    gql`
      query duelingBookStatus($tournamentId: Int!) {
        data: duelingBookStatus(tournamentId: $tournamentId) {
          tournamentId
          tourneyId
          tourneyUrl
          challongeUrl
          remote {
            name
            started
            ended
            participants
            lastRound
            error
          }
          players {
            state
            userId
            name
            ygoaStatus
            duelingBookId
            dbUsername
          }
          pendingInvitations
          lastSyncAt
          syncedMatches
        }
      }
    `,
    { tournamentId }
  );

/** Lo mismo que corre el worker cada 10 minutos, a demanda. Admin. */
export const syncDuelingBookTournament = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<DuelingBookSyncResult>>(
    gql`
      mutation syncDuelingBookTournament($tournamentId: Int!) {
        data: syncDuelingBookTournament(tournamentId: $tournamentId) {
          created
          updated
          currentRound
        }
      }
    `,
    { tournamentId }
  );
